import { ZButton } from "components/ZButton";
import { ZTextInput } from "components/ZTextInput";
import { ZTooltip } from "components/ZTooltip";
import { useEffect, useState } from "react";
import { Col, Form, Modal, Row } from "react-bootstrap";
import { Controller, FieldErrors, useForm } from "react-hook-form";
import { useAppDispatch } from "redux/hooks";
import { importFromProductsWithData, linkToProduct } from "redux/slices/Stocks";
import { ManagedStockMerge } from "../../ManagedStockMerge";
import { ErrorCase, ErrorHandler, ErrorInformation, getErrorCase, getModelDataForImport } from "./ErrorHandler";
import { useNavigate } from "react-router";
import { ManagedStockApn } from "models/dbModels/managedStock/ManagedStockApn";
import { ManagedStockView } from "models/dbModels/managedStock/ManagedStock";
import { CheckDuplicateApnPde } from "redux/slices/ApnPde";

type ApnFormProps = {
	selectedApn?: ManagedStockApn;
	apns?: ManagedStockApn[];
	stockModel: ManagedStockView;
	formErrors: FieldErrors<ManagedStockView>;
	showPopup?: boolean;
	onHide?: () => void;
	onSubmit?: (apn: ManagedStockApn) => void;
}

type ValidationFormProps = { 
	apn: string;
}

export default function APN({ selectedApn, apns, showPopup, onHide, onSubmit, stockModel, formErrors }: ApnFormProps) {
	const initialFormApn: ManagedStockApn = {
		id: 0,
		managedStockId: 0,
		apn: "",
		isSystem: false,
		systemId: null
	}
	const [submitting, setSubmitting] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const defaultValues: ValidationFormProps = {
		apn: ""
	}
	const { 
		handleSubmit, 
		control,
		reset,
		getValues,
		setError,
		formState: { errors },
	} = useForm<ValidationFormProps>({
		defaultValues: defaultValues,
		mode: "onChange",
		reValidateMode: "onChange",
	})

	const [formApn, setFormApn] = useState<ManagedStockApn>(initialFormApn);
	const [prevApn, setPrevApn] = useState<string>("");
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const [showErrorPopup, setShowErrorPopup] = useState<boolean>(false);
	const [showManualMergePopup, setShowManualMergePopup] = useState<boolean>(false);
	const [mergeStockIds, setMergeStockIds] = useState<number[]>([]);

	const [apnErrorInfo, setApnErrorInfo] = useState<ErrorInformation>();
	
	useEffect(() => {
		if (showPopup) {
			if (selectedApn != null) {
				setFormApn(selectedApn);
				setPrevApn(selectedApn.apn);
				reset({
					apn: selectedApn.apn,
				} as ValidationFormProps);
			} else {
				resetFormApn();
			}
		}
	}, [showPopup]);
	

	async function handleFormSubmit() {
		handleSubmit(async (validData) => {
			setSubmitting(true);
			// Validation success
			const formValues = getValues();
			const isDuplicate = await isDuplicateApn(formValues.apn);
			setSubmitting(false);
			if (isDuplicate) {
				setError("apn", { type: "custom", message: `This APN already exists!`});
			} else {
				// Success! Let's save the APN...
				onSubmit && onSubmit({
					...formApn,
					apn: formValues.apn
				});
				resetFormApn();
			}
		},
		(invalidData) => {
			// Validation fail
		})();
	}

	function handleFormCancel() {
		resetFormApn();
		onHide && onHide();
	}

	async function isDuplicateApn(apn: string) {
		if (apn === prevApn) return false;
		// Early check against existing apns in the table on the managed stock.
		if (apns?.some(a => a.apn === apn)) return true;

		let isDupe = false;
		await dispatch(CheckDuplicateApnPde({
			filters: [{
				apn: apn
			}],
			queryType: 'All'
		}))
		.then((res) => {
			if (res.data && res.data.length !== 0) {
				// Common data:
				// 	- stockModel
				//	- dupe structure
				//  - cases are triggered the same way
				// We have duplicates
				// Set up error variables
				const dupe = res.data[0]; // We shouldn't be getting multiple from this endpoint
				let errorInfo: ErrorInformation | undefined = undefined;
				const errorCase = getErrorCase(dupe, stockModel);
				// There are several cases we have to consider

				switch(errorCase) {
					case ErrorCase.UpdateSystem_System:
					case ErrorCase.New_System:
						const subset = getModelDataForImport(formErrors, stockModel);

						errorInfo = {
							heading: "APN already exists on system stock",
							messages: [
								`The system stock ${dupe.parentModelName} already exists with the APN ${apn}.`,
								"Would you like to import it now?"
							],
							buttonInfo: {
								buttonText: "Import",
								loadingButtonText: "Importing...",
								buttonAction: async () => {
									setLoading(true);
									const response = await dispatch(importFromProductsWithData([
										{ ...subset, productId: dupe.parentId }
									]));
									// We expect back a list of ids corresponding to the created managed stocks, since we are sending a single import, we expect a single id back
									// Use this id to navigate
									setLoading(false);
									handleFormCancel();
									navigate(`/app/stock/${response.data[0]}`);
								},
								loading: loading
							}
						}
						break;
					case ErrorCase.New_SystemManaged:
					case ErrorCase.New_CustomManaged:
						errorInfo = {
							heading: "APN already exists on managed stock",
							messages: [
								`A managed stock already exists with APN ${apn}.`,
								"Would you like to navigate there now?"
							],
							link: `/app/stock/${dupe.parentId}`,
							buttonInfo: {
								buttonText: "Go to stock",
								buttonAction: () => {
									handleFormCancel();
									navigate(`/app/stock/${dupe.parentId}`);
								}
							}
						}
						break;
					case ErrorCase.UpdateSystem_SystemManaged:
						errorInfo = {
							heading: "APN already exists on managed stock",
							messages: [
								`A managed stock already exists with APN ${apn}.`,
								"Would you like to navigate there now?"
							],
							link: `/app/stock/${dupe.parentId}`,
							buttonInfo: {
								buttonText: "Go to stock",
								buttonAction: () => {
									handleFormCancel();
									navigate(`/app/stock/${dupe.parentId}`);
								}
							}
						}
						break;
					case ErrorCase.UpdateSystem_CustomManaged:
						errorInfo = {
							heading: "APN already exists on managed stock",
							messages: [
								`A managed stock already exists with APN ${apn}.`,
								"Would you like to merge your existing managed stock to this one?"
							],
							buttonInfo: {
								buttonText: "Merge",
								loadingButtonText: "Merging...",
								buttonAction: () => {
									setLoading(true);
									setMergeStockIds([stockModel.id, dupe.parentId]);
									setShowManualMergePopup(true);
								},
								loading: loading
							}
						}
						break;
					case ErrorCase.UpdateCustom_System:
						errorInfo = {
							heading: "APN already exists on system stock",
							messages: [
								`A system stock already exists with APN ${apn}.`,
								"Would you like to link your existing managed stock to this system one?"
							],
							buttonInfo: {
								buttonText: "Link",
								loadingButtonText: "Linking...",
								buttonAction: () => {
									setLoading(true);
									dispatch(linkToProduct([
										{
											productId: dupe.parentId,
											managedId: stockModel.id
										}
									])).then(() => {
										setLoading(false);
										handleFormCancel();
										navigate(`/app/stock/${stockModel.id}`);
									});
								},
								loading: loading
							}
						}
						break;
					case ErrorCase.UpdateCustom_SystemManaged:
					case ErrorCase.UpdateCustom_CustomManaged:
						errorInfo = {
							heading: "APN already exists on managed stock",
							messages: [
								`A managed stock already exists with APN ${apn}.`,
								"Would you like to merge your existing managed stock to this one?"
							],
							buttonInfo: {
								buttonText: "Merge",
								loadingButtonText: "Merging...",
								buttonAction: () => {
									setLoading(true);
									setMergeStockIds([stockModel.id, dupe.parentId]);
									setShowManualMergePopup(true);
								},
								loading: loading
							}
						}
						break;
				}
				
				setApnErrorInfo(errorInfo);
				setShowErrorPopup(true);
				isDupe = true;
			}
		});
		return isDupe;
		return isDupe;
	}

	const RequiredComponent = (text: string, tooltipText: string) => {
		return (
			<div className="d-inline-flex align-items-center gap-1">
				{text}
				<ZTooltip enabled placement='top' text={tooltipText}>
					<>*</>
				</ZTooltip>
			</div>
		);
	}
	
	function resetFormApn() {
		reset(defaultValues);
		setFormApn(initialFormApn);
		setApnErrorInfo(undefined);
		setShowErrorPopup(false);
		setShowManualMergePopup(false);
		setMergeStockIds([]);
	}

	function handleKeyPress(e: React.KeyboardEvent<HTMLInputElement>) {
		if (e.key === "Enter") {
			e.stopPropagation();
			e.preventDefault();
		}
	}

	return (
		<>
		{ showManualMergePopup && <ManagedStockMerge
			loadStocks
			show={showManualMergePopup}
			stockIds={mergeStockIds}
			handleClose={() => setShowManualMergePopup(false)}
			afterMerge={() => {
				// TODO: Nav to newly merged stock??
				setLoading(false);
				handleFormCancel();
				// TODO: This is maybe not the right id
				navigate(`/app/stock/${stockModel.id}`);
			}}
		/>}
		{!showManualMergePopup && <ErrorHandler errorInfo={apnErrorInfo} showError={showErrorPopup} onBack={() => setShowErrorPopup(false)} />}
		{!showErrorPopup && !showManualMergePopup && <Modal
			size="sm"
			centered
			backdrop="static"
			show={showPopup}
			onHide={() => {
				resetFormApn();
				onHide && onHide();
			}}
		>
			<Modal.Header closeButton>
				<Modal.Title>APN</Modal.Title>
			</Modal.Header>
			<Modal.Body onKeyDown={handleKeyPress}>
				<Row className="mb-1">
					<Col>
						<Form>
							<Row>
								<Col md={12}>
									<Controller
										control={control}
										rules={{
											required: {
												value: true,
												message: "Please enter an APN"
											},
										}}
										render={({ field: { onChange, onBlur, value } }) =>
											<ZTextInput
												autoFocus
												disabled={formApn.isSystem}
												label={RequiredComponent("APN", "This field is required")}
												placeholder="Enter an APN"
												value={value}
												onChange={onChange}
												onBlur={onBlur}
												validation={
													{
														invalid: errors.apn != null,
														error: errors.apn?.message
													}
												}
											/>
										}
										name='apn'
									/>
								</Col>
							</Row>
							<Row className='mt-3'>
								<Col sm={6}>
								</Col>
								<Col sm={6} className="d-flex flex-row-reverse gap-2">
									<ZButton loading={submitting} variant="primary" buttonText={submitting ? `Saving...` : `Save`} onClick={handleFormSubmit} />
									<ZButton variant="light" buttonText='Cancel' onClick={handleFormCancel} />
								</Col>
							</Row>
						</Form>
					</Col>
				</Row>
			</Modal.Body>
		</Modal>}
		</>
	);
}
